<template>
  <div
    ref="QRElementRef"
    spellcheck="false"
    class="group relative m-2 h-[37rem] w-[26rem] overflow-hidden border bg-[#F39200]"
  >
    <div class="absolute -top-72 right-0 h-5/6 w-28 rotate-[100deg] scale-150 transform bg-[#ED7100]" />

    <div class="absolute -bottom-72 left-0 h-full w-28 rotate-[140deg] transform bg-[#ED7100]" />

    <div class="absolute -bottom-72 right-0 h-full w-28 rotate-[50deg] transform bg-[#ED7100]" />

    <div class="relative mx-5 mt-10">
      <h1
        contenteditable="true"
        :data-placeholder="t('emptyTitle')"
        class="mx-4 flex h-32 flex-col justify-center overflow-hidden break-words border-2 border-transparent p-2 text-center text-neutral-100 focus:outline-none group-hover:border-dashed group-hover:border-neutral-40 group-hover:focus:border-primary-40"
        :class="[title && titleFontSize, title ? 'font-semibold' : 'opacity-40']"
        @input="UpdateTitle"
      >
        {{ title }}
      </h1>
      <LoadingSpinner v-if="loading" class="h-6 w-6 text-primary-40" aria-hidden="true" />

      <img v-else-if="qrDataImage" :src="qrDataImage" class="mx-auto h-52 w-52" />
      <div v-else class="mx-auto flex h-52 w-52 items-center justify-center rounded border-2 border-neutral-40">QR</div>
      <h1
        contenteditable="true"
        :data-placeholder="t('emptyTag')"
        class="mx-8 h-8 overflow-hidden border-2 border-transparent text-center text-sm text-[#9B4A00] focus:outline-none group-hover:border-dashed group-hover:border-neutral-40 group-hover:focus:border-primary-40"
        @input="UpdateTag"
      >
        {{ tag }}
      </h1>
      <h1
        contenteditable="true"
        :data-placeholder="t('emptyDescription')"
        class="mx-6 mt-4 h-16 break-words rounded border-2 border-transparent text-center text-neutral-100 focus:outline-none group-hover:border-dashed group-hover:border-neutral-40 group-hover:focus:border-primary-40"
        :class="[
          description && descriptionFontSize,
          description ? 'font-light' : 'opacity-40',
          pdfMode ? 'overflow-visible pb-4' : 'overflow-hidden',
        ]"
        @input="UpdateDescription"
      >
        {{ description }}
      </h1>
      <div v-if="!windowConfig.whiteLabel" class="flex justify-center">
        <PerfitLogo class="h-16 w-16" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { watchEffect, ref, computed, inject } from "vue";
import QRCode from "qrcode";

//Components
import LoadingSpinner from "@atoms/LoadingSpinner.vue";

//Logo
import PerfitLogo from "../PerfitLogo.vue";

//Utils
import { useNotifications } from "@composables/notifications";
import { useI18n } from "vue-i18n";

const { notify } = useNotifications();
const { t } = useI18n();

//Props
const props = withDefaults(
  defineProps<{
    title?: string;
    description?: string;
    tag?: string;
    url: string;
    qrDataImage: string;
  }>(),
  {
    title: "",
    description: "",
    tag: "",
    url: "",
    qrDataImage: "",
  },
);

//Emits
const emit = defineEmits<{
  (e: "update:title", modelValue: string): void;
  (e: "update:description", modelValue: string): void;
  (e: "update:tag", modelValue: string): void;
  (e: "update:qrDataImage", modelValue: string): void;
}>();

const pdfMode = inject("pdfMode", false);
const windowConfig = computed(() => window.config);

const maxTitleLength = 240;
const UpdateTitle = (event: Event) => {
  const text = (event.target as HTMLHeadElement)?.textContent;

  if (text && text.length > maxTitleLength) (event.target as HTMLHeadElement).textContent = props.title;
  emit("update:title", (event.target as HTMLHeadElement)?.textContent || "");
};

const maxDescriptionLength = 120;
const UpdateDescription = (event: Event) => {
  const text = (event.target as HTMLHeadElement)?.textContent;

  if (text && text.length > maxDescriptionLength) (event.target as HTMLHeadElement).textContent = props.description;
  emit("update:description", (event.target as HTMLHeadElement)?.textContent || "");
};

const maxTagLength = 40;
const UpdateTag = (event: Event) => {
  const text = (event.target as HTMLHeadElement)?.textContent;

  if (text && text.length > maxTagLength) (event.target as HTMLHeadElement).textContent = props.tag;
  emit("update:tag", (event.target as HTMLHeadElement)?.textContent || "");
};

//Refs
const QRElementRef = ref();

//State
const loading = ref(false);

//Functions
const generateQR = async (text) => {
  try {
    loading.value = true;

    const QRImage = await QRCode.toDataURL(text, {
      errorCorrectionLevel: "H",
      scale: 12,
      margin: 2,
      color: {
        dark: "#ffffff",
        light: "#F39200",
      },
    });

    emit("update:qrDataImage", QRImage);

    loading.value = false;
  } catch (err) {
    loading.value = false;
    notify({
      title: t(`errors.server.title`),
      text: t(`errors.server.text`),
      theme: "error",
      timeout: 0,
    });
  }
};

const descriptionFontSize = computed(() => {
  if (props.description.length >= 100) {
    return "text-xs";
  }
  if (props.description.length >= 80) {
    return "text-sm";
  }
  if (props.description.length >= 60) {
    return "text-base";
  }
  if (props.description.length >= 40) {
    return "text-lg";
  }
  return "text-xl";
});

const titleFontSize = computed(() => {
  if (props.title.length >= 180) {
    return "text-xs";
  }
  if (props.title.length >= 120) {
    return "text-sm";
  }
  if (props.title.length >= 100) {
    return "text-base";
  }
  if (props.title.length >= 80) {
    return "text-lg";
  }
  if (props.title.length >= 60) {
    return "text-xl";
  }
  if (props.title.length >= 40) {
    return "text-2xl";
  }
  return "text-3xl";
});

//Watchers
watchEffect(async () => {
  if (props.url) {
    await generateQR(props.url);
  } else {
    emit("update:qrDataImage", "");
  }
});
</script>

<style>
[contentEditable="true"]:empty:not(:focus):before {
  content: attr(data-placeholder);
}
</style>

<i18n lang="jsonc">
// TODO: i18n
{
  "es": {
    "emptyTitle": "Titulo",
    "emptyDescription": "Descripción",
  },
  "pt": {
    "emptyTitle": "Título",
    "emptyDescription": "Descrição",
  },
}
</i18n>
